import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AuthorizationService } from '@services/authorization.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  protected http = inject(HttpClient);
  protected storage = inject(StorageService);
  protected authService = inject(AuthorizationService);
  protected orderService = inject(OrderService);

  /**
   * Checks if the account is associated with a business account and updates the isBaOrder state.
   *
   * @param {string} token the JWT token to check for business account association
   *
   * @returns {Observable<number | null>} observable that emits the business account ID, or null if not a BA
   */
  public isBusinessAccount(token: string): Observable<number | null> {
    const isPotentialBa = environment.enableBusinessAccounts && !!token;

    if (!isPotentialBa && this.storage.baOrder?.isActive) {
      this.orderService.clearLastOrder();
    }

    return isPotentialBa
      ? this.getBusinessAccountId(token).pipe(
          map((response) => response.business_account_id ?? null),
          catchError(() => of(null)),
        )
      : of(null);
  }

  /**
   * Get the business account id from STDcheck
   *
   * @param {string} token The JWT token to validate the business account
   *
   * @returns the request body to make a get request to /api/v1/account
   */
  private getBusinessAccountId(token: string): Observable<{ business_account_id: number }> {
    return this.http.get<{ business_account_id: number }>(
      `${environment.analyteCareApi}/api/v1/account`,
      this.authService.getOptionsWithAuthHeader(token),
    );
  }
}
